import { type ChangeEvent, type JSX, useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { type ZendeskPlatformModel } from "@doitintl/cmp-models";
import { Box, MenuItem, Tooltip, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import uniq from "lodash/uniq";

import { supportPlatformDropdown } from "../../constants/cypressIds";
import { useCustomerContext } from "../../Context/CustomerContext";
import { useIsFeatureEntitledMultiple, useTier } from "../../Context/TierProvider";
import { isCustomerInPresentationMode } from "../../Context/useCustomerOrPresentationModeCustomer";
import { type Asset } from "../../types";
import { entitlementsToContactSupport } from "../consts";
import { useGetCustomerContractAndAssetDetails } from "../hooks/useGetCustomerContractAndAssetDetails";
import { useIsDCIEssentialsTier } from "../hooks/useIsDCIEssentialsTier";
import { isDoitConsoleBaseService, isStandaloneAppPlatform, platformValue } from "../utils";
import SupportPlatformMenuItemContent from "./SupportPlatformMenuItemContent";
import UnlockSupportOptionsDropdownBox from "./UnlockSupportOptionsDropdownBox";

type Props = {
  platform: string;
  error: boolean;
  platformsList: ZendeskPlatformModel[];
  handleChangePlatformField: (data: ChangeEvent) => void;
  hideHelper?: boolean;
};

const SupportPlatformDropdown = ({ platform, error, platformsList, handleChangePlatformField, hideHelper }: Props) => {
  const { assets, isProductOnlyCustomer, customer } = useCustomerContext({
    allowNull: true,
  });

  const isInPresentationMode = customer && isCustomerInPresentationMode(customer);

  const { tiers } = useTier();
  const { isStandaloneAppWithoutContractOrAssets, hasActiveContract, customerDoesNotHaveAssets } =
    useGetCustomerContractAndAssetDetails();
  const { t } = useTranslation("services");
  const supportEntitlementsCheck = useIsFeatureEntitledMultiple(entitlementsToContactSupport);

  const isDCIEssentialsTier = useIsDCIEssentialsTier();
  const [supportedPlatforms, setSupportedPlatforms] = useState<ZendeskPlatformModel[]>([]);

  const [alsoSupportedPlatforms, setAlsoSupportedPlatforms] = useState<ZendeskPlatformModel[]>([]);

  const isAllPlatformsEntitled = !!supportEntitlementsCheck["support:ticket:menu:platform:all"] || isInPresentationMode;

  const { headers, items } = supportPlatformDropdown.contents;

  useEffect(() => {
    const allAssets: Asset[] = assets ? Object.values(assets).flatMap((a) => a) : [];
    const types: string[] = uniq(allAssets.map((asset) => platformValue(asset.data.type))).sort();
    let supportedZendeskPlatforms = platformsList.filter(
      (p) => types.includes(platformValue(p.asset)) || p.asset === "perfectscale"
    );

    // order platforms by order field
    supportedZendeskPlatforms = supportedZendeskPlatforms.sort((a, b) => (a.order || 0) - (b.order || 0));

    // Add DoiT Platform to support list, if it wasn't detected in the asset types.
    const doitPredicate = (p: ZendeskPlatformModel) => p.value === "cloud_management_platform";
    if (!supportedZendeskPlatforms.find(doitPredicate)) {
      const doitPlatform: ZendeskPlatformModel | undefined = platformsList.find(doitPredicate);
      doitPlatform && supportedZendeskPlatforms.unshift(doitPlatform);
    }

    if (!isProductOnlyCustomer) {
      // Add finance platform to supported list, if it wasn't detected in asset types.
      const hasFinancePredicate = (p: ZendeskPlatformModel) => p.value === "finance___billing";
      if (!supportedZendeskPlatforms.find(hasFinancePredicate)) {
        const financePlatform: ZendeskPlatformModel = platformsList.find(hasFinancePredicate) || {
          label: "Account, domain or invoice ID",
          title: "Invoices and Finance",
          order: 20,
          value: "finance___billing",
          helperText: "This will help us to address your request more efficiently",
          asset: "finance-billing",
        };
        supportedZendeskPlatforms.push(financePlatform);
      }

      // Add credits platform to supported list if it wasn't detected in asset types.
      const hasCreditsPredicate = (p: ZendeskPlatformModel) => p.value === "credits___request";
      if (!supportedZendeskPlatforms.find(hasCreditsPredicate)) {
        const creditsPlatform: ZendeskPlatformModel = platformsList.find(hasCreditsPredicate) || {
          label: "Request promotional credits",
          title: "Request promotional credits",
          order: 30,
          value: "credits___request",
          helperText: "",
          asset: "promotional-credits",
        };
        supportedZendeskPlatforms.push(creditsPlatform);
      }
    }
    setSupportedPlatforms(supportedZendeskPlatforms);

    const alsoSupportedZendeskPlatforms = platformsList.filter((p) => !supportedZendeskPlatforms.includes(p));
    setAlsoSupportedPlatforms(alsoSupportedZendeskPlatforms);
  }, [assets, isProductOnlyCustomer, platformsList]);

  const servicesSectionHeader = (
    <Box sx={{ ml: 2, height: "32px", pt: 0.5 }} data-cy={headers.customerServices}>
      <Typography variant="caption">{t("SupportPlatformDropdown.services")}</Typography>
    </Box>
  );
  const alsoSupportedSectionHeader = (
    <Box sx={{ ml: 2, height: "32px" }} data-cy={headers.alsoSupported}>
      <Typography variant="caption">We also support</Typography>
    </Box>
  );

  const makeMenuItem = useCallback<(p: ZendeskPlatformModel, customerService: boolean) => JSX.Element>(
    (p: ZendeskPlatformModel, customerService: boolean): JSX.Element => {
      const hasPlatformSupportEntitlement = (platform: string): boolean => {
        const allPlatform = `support:ticket:menu:platform:${platform}`;
        return isDoitConsoleBaseService(platform) || isAllPlatformsEntitled || supportEntitlementsCheck[allPlatform];
      };
      const featureUnlocked = hasPlatformSupportEntitlement(p.asset.replace("-project", ""));

      const showAlwaysEnabled =
        p.asset === "perfectscale" ||
        p.asset === "finance-billing" ||
        p.asset === "promotional-credits" ||
        isInPresentationMode;

      const isMissingAssets = customerDoesNotHaveAssets(p.asset) && !showAlwaysEnabled;
      const hasNoActiveContract = !hasActiveContract(p.asset);

      const overrideIfDisabled = !hasNoActiveContract && isStandaloneAppPlatform(p.asset);

      const keyBaseMenuItem = p.value;
      const baseMenuItemProps = {
        value: p.value,
        "data-cy": items[p.value],
        sx: { height: "40px" },
        "data-section": customerService ? "customer-services" : "also-supported",
      } as const;

      const keyBaseContent = p.asset;
      const baseContentProps = {
        overrideIfDisabled: !!overrideIfDisabled,
        zendPlatform: p,
        dataCyItems: items,
      };

      const disabledProps = { color: "text.disabled", pointerEvents: "none" };

      const disabledMenuItem = (disabledByEntitlement: boolean): JSX.Element => {
        const baseDisabledMenuItem = (
          <MenuItem {...baseMenuItemProps} key={keyBaseMenuItem} sx={disabledProps} aria-disabled>
            <SupportPlatformMenuItemContent {...baseContentProps} key={keyBaseContent} />
          </MenuItem>
        );

        if (disabledByEntitlement) {
          const CustomTooltipTitle = () => (
            <Typography sx={{ textAlign: "center", fontSize: "10px", fontWeight: 500, lineHeight: "14px" }}>
              Ask Ava, our AI-powered virtual assistant <br /> or upgrade to the Enhanced tier or above
            </Typography>
          );
          return (
            <Tooltip title={<CustomTooltipTitle />} key={p.value} sx={{ textAlign: "center" }}>
              <Box>{baseDisabledMenuItem}</Box>
            </Tooltip>
          );
        }

        return baseDisabledMenuItem;
      };
      const EnabledMenuItem: JSX.Element = (
        <MenuItem {...baseMenuItemProps} key={keyBaseMenuItem} sx={isMissingAssets ? disabledProps : {}}>
          <SupportPlatformMenuItemContent {...baseContentProps} key={keyBaseContent} />
        </MenuItem>
      );

      // if has active contract for standalone apps
      if (overrideIfDisabled) {
        return EnabledMenuItem;
      }

      return (!featureUnlocked || isStandaloneAppWithoutContractOrAssets(p.asset)) &&
        !isDCIEssentialsTier &&
        !showAlwaysEnabled
        ? disabledMenuItem(!featureUnlocked)
        : EnabledMenuItem;
    },
    [
      hasActiveContract,
      isAllPlatformsEntitled,
      isStandaloneAppWithoutContractOrAssets,
      items,
      supportEntitlementsCheck,
      isDCIEssentialsTier,
      customerDoesNotHaveAssets,
      isInPresentationMode,
    ]
  );

  const allZendeskPlatforms = [...supportedPlatforms, ...alsoSupportedPlatforms];
  const currentValueValid = allZendeskPlatforms.some((p) => p.value === platform);

  return (
    <TextField
      select
      name="platform"
      label={t("SupportPlatformDropdown.label")}
      required
      helperText={hideHelper ? "" : t("SupportPlatformDropdown.helper")}
      variant="outlined"
      value={currentValueValid ? platform : ""}
      onChange={handleChangePlatformField}
      error={error && !platform}
      fullWidth
      size="medium"
      data-cy={supportPlatformDropdown.root}
      slotProps={{
        select: {
          MenuProps: {
            sx: { maxHeight: "460px", maxWidth: "300px", zIndex: 1201 },
          },
        },
      }}
    >
      {servicesSectionHeader}
      {supportedPlatforms.map((platform) => makeMenuItem(platform, true))}
      {!tiers && !!alsoSupportedPlatforms.length && alsoSupportedSectionHeader}
      {alsoSupportedPlatforms.map((platform) => makeMenuItem(platform, false))}
      {!!tiers && !isAllPlatformsEntitled && <UnlockSupportOptionsDropdownBox />}
    </TextField>
  );
};

export default SupportPlatformDropdown;
